import { useEffect, useState } from 'react';
import type { Chapter } from '@/shared/types';
import { parseTitleHints, formatChapterSuggestion } from '@/shared/text';
import { Button } from '@/components/Button';
import { Card } from '@/components/Card';
import { Input, Select } from '@/components/Field';

export interface VttCapture {
  id: string;
  vttUrl: string;
  tabId: number;
  tabTitle: string;
  pageTitle?: string;
  tabUrl: string;
  capturedAt: number;
}

export interface IngestArgs {
  capture: VttCapture;
  chapterTitle: string;
  targetChapterId?: string;
}

export function VttCaptureRow({
  capture,
  chapters,
  busy,
  disabled,
  onIngest,
}: {
  capture: VttCapture;
  chapters: Chapter[];
  busy: boolean;
  disabled: boolean;
  onIngest: (args: IngestArgs) => void;
}) {
  // Prefer the on-page H2 (CeriFi puts the video title there) over the tab
  // title, which is usually a generic "Class Video Player".
  const titleSource = capture.pageTitle || capture.tabTitle;
  const hints = parseTitleHints(titleSource);
  const suggested =
    hints.chapterTitle || hints.chapterNum !== undefined
      ? formatSuggestion(hints, titleSource)
      : titleSource || 'Untitled video';

  const [chapterTitle, setChapterTitle] = useState(suggested);
  const [target, setTarget] = useState<'new' | string>('new');

  const matchingExisting = chapters.find((c) => normalize(c.title) === normalize(suggested));
  // Default to attaching to a matching existing chapter when we find one — that's
  // the common case for Tish: she'll capture the chapter text first, then come
  // back to add the lecture video VTT under the same chapter.
  useEffect(() => {
    if (matchingExisting && target === 'new') setTarget(matchingExisting.id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [matchingExisting?.id]);

  const ageSec = Math.round((Date.now() - capture.capturedAt) / 1000);
  const tabHost = safeHost(capture.tabUrl || capture.vttUrl);
  const vttHost = safeHost(capture.vttUrl);

  return (
    <Card padding="none" className="p-3">
      <div className="mb-2 space-y-0.5">
        <div
          className="text-sm font-medium text-slate-800"
          title={capture.pageTitle || capture.tabTitle}
        >
          {capture.pageTitle || capture.tabTitle || '(untitled tab)'}
        </div>
        <div className="text-[10px] text-slate-500">
          {ageSec < 90 ? `${ageSec}s ago` : `${Math.round(ageSec / 60)}m ago`}
          {tabHost && (
            <>
              {' '}
              · tab on <span className="font-mono">{tabHost}</span>
            </>
          )}
          {vttHost && vttHost !== tabHost && (
            <>
              {' '}
              · vtt from <span className="font-mono">{vttHost}</span>
            </>
          )}
        </div>
      </div>

      <label className="block text-xs">
        <span className="text-slate-700">Chapter title</span>
        <Input
          value={chapterTitle}
          onChange={(e) => setChapterTitle(e.target.value)}
          className="mt-0.5 w-full"
        />
      </label>

      <label className="mt-2 block text-xs">
        <span className="text-slate-700">Attach to</span>
        <Select
          value={target}
          onChange={(e) => setTarget(e.target.value)}
          className="mt-0.5 w-full"
        >
          <option value="new">+ New chapter</option>
          {chapters.map((c) => (
            <option key={c.id} value={c.id}>
              {c.title}
            </option>
          ))}
        </Select>
      </label>

      <Button
        variant="primary"
        block
        className="mt-2"
        onClick={() =>
          onIngest({
            capture,
            chapterTitle: chapterTitle || capture.tabTitle || 'Captured video',
            targetChapterId: target === 'new' ? undefined : target,
          })
        }
        disabled={disabled}
        loading={busy}
      >
        {busy
          ? 'Fetching transcript…'
          : target === 'new'
            ? 'Save as new chapter'
            : 'Attach transcript to chapter'}
      </Button>

      <details className="mt-2 text-[10px] text-slate-500">
        <summary className="cursor-pointer">VTT URL</summary>
        <code className="mt-1 block break-all">{capture.vttUrl}</code>
      </details>
    </Card>
  );
}

const formatSuggestion = formatChapterSuggestion;

function normalize(s: string): string {
  return s.toLowerCase().replace(/[^a-z0-9]+/g, ' ').trim();
}

function safeHost(url: string): string {
  try {
    return new URL(url).hostname;
  } catch {
    return '';
  }
}
